import { mdiWindowClose } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { Subscription } from "rxjs";
import styled from "styled-components/macro";
import { idpc } from "../api";
import { Button } from "../Button";
import {
  AuthContext,
  EmailAddress as EmailAddressProto,
  EmailEnrollment,
  SendAuthMailRequest_Type,
  User,
  WatchEnrolmentEvent,
  WatchEnrolmentRequest_Type,
} from "../generated/idp/api/idp";
import { EmailEnroll } from "../login/Mail";
import { Spinner } from "../Spinner";
import { ConfirmationModal } from "./ConfirmationModal";
import { mdiDeleteOutline, mdiEmail, mdiShieldCheck } from "@mdi/js";
import { lighten } from "polished";
import { EmailAddress, Trust } from "../generated/idp/api/idp";

const FaList = styled.div`
  margin-top: 10px;
  max-width: 300px;
`;

const customStyles: Modal.Styles = {
  content: {
    fontFamily: "sans-serif",
    maxWidth: 500,
    position: "unset",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const Title = styled.h2`
  margin-bottom: 6px;
  margin-top: 25px;
`;

const ModalTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  height: 100%;
  padding-top: 5px;
`;

export function EmailManager(props: {
  user: User;
  authenticate: (a: AuthContext) => void;
  onUpdate?: () => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EmailAddressProto | null>(null);
  const [verifyingEmail, setVerifyingEmail] = useState<EmailAddressProto | null>(null);
  const [verificationState, setVerificationState] = useState<
    "init" | "sending" | "waiting" | "failed"
  >("init");
  const [verificationError, setVerificationError] = useState<string | null>(null);

  const handleDeleteRequest = useCallback((email: EmailAddressProto) => {
    setPendingDelete(email);
    setDeleteConfirmModalOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDelete) {
      try {
        await idpc.RemoveMail({ id: pendingDelete.id });
        props.onUpdate?.();
      } catch (err) {
        alert("Fehler beim Löschen der E-Mail: " + err);
      } finally {
        setDeleteConfirmModalOpen(false);
        setPendingDelete(null);
      }
    }
  }, [pendingDelete, props]);

  const handleVerifyRequest = useCallback(async (email: EmailAddressProto) => {
    setVerifyingEmail(email);
    setVerificationState("sending");
    setVerificationError(null);
    try {
      await idpc.SendAuthMail({
        id: email.id,
        type: SendAuthMailRequest_Type.VERIFICATION,
      });
      setVerificationState("waiting");
    } catch (err) {
      console.error(err);
      setVerificationError("Es ist ein Fehler beim Senden der Bestätigungs-E-Mail aufgetreten.");
      setVerificationState("failed");
    }
  }, []);

  const onDone = props.onUpdate;
  useEffect(() => {
    if (verificationState === "waiting") {
      let sub: Subscription | null = null;
      let done = false;
      const watcher = () => {
        const w = idpc.WatchEnrolment({
          type: WatchEnrolmentRequest_Type.EMAIL,
        });
        sub = w.subscribe({
          next(e: WatchEnrolmentEvent) {
            if (e.ok) {
              sub?.unsubscribe();
              setVerificationState("init");
              setVerifyingEmail(null);
              onDone?.();
              return;
            }
          },
          error(e) {
            console.log(e);
            if (!done) {
              setTimeout(watcher, 1000);
            }
          },
          complete() {
            return;
          },
        });
      };
      watcher();
      return () => {
        done = true;
        sub?.unsubscribe();
      };
    }
  }, [verificationState, onDone]);

  const canDelete = (props.user.emailAddress?.length ?? 0) > 1;

  return (
    <div>
      <Title>E-Mail</Title>
      <Modal isOpen={modalOpen} onRequestClose={() => setModalOpen(false)} style={customStyles}>
        <ModalTitle>
          <h1>E-Mail hinzufügen</h1>
          <CloseButton onClick={() => setModalOpen(false)}>
            <Icon path={mdiWindowClose} size={1} />
          </CloseButton>
        </ModalTitle>
        <EnrolmentWrapper />
      </Modal>

      <Modal
        isOpen={verificationState !== "init"}
        onRequestClose={() => {
          setVerificationState("init");
          setVerifyingEmail(null);
        }}
        style={customStyles}
      >
        <ModalTitle>
          <h1>E-Mail verifizieren</h1>
          <CloseButton
            onClick={() => {
              setVerificationState("init");
              setVerifyingEmail(null);
            }}
          >
            <Icon path={mdiWindowClose} size={1} />
          </CloseButton>
        </ModalTitle>
        {verificationState === "sending" ? (
          <>
            <Spinner color="black" /> Verifikations-E-Mail wird versendet...
          </>
        ) : null}
        {verificationState === "waiting" && verifyingEmail ? (
          <>
            <p style={{ marginTop: "15px" }}>
              Eine Verifikations-E-Mail wurde an{" "}
              {`${verifyingEmail.localPart}@${verifyingEmail.domain}`} versendet. Bitte folgen Sie
              den Instruktionen in der E-Mail um die Verifikation abzuschliessen.
            </p>
            <p>
              Sie werden automatisch weitergeleitet, sobald Sie die Verifikation abgeschlossen
              haben.
            </p>
            <p style={{ textAlign: "center", marginTop: 25, marginBottom: 25 }}>
              <Spinner color="black" />
            </p>
          </>
        ) : null}
        {verificationState === "failed" && <p style={{ color: "red" }}>{verificationError}</p>}
      </Modal>

      <FaList>
        {props.user.emailAddress?.map((ea) => (
          <EmailItem
            key={ea.id.toString()}
            spec={ea}
            onDelete={() => handleDeleteRequest(ea)}
            onVerify={() => handleVerifyRequest(ea)}
            canDelete={canDelete}
          />
        ))}
      </FaList>

      <Button onClick={() => setModalOpen(true)}>E-Mail Hinzufügen</Button>

      <ConfirmationModal
        isOpen={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onConfirm={confirmDelete}
        title="E-Mail löschen"
        message={`Möchten Sie die E-Mail "${
          pendingDelete ? `${pendingDelete.localPart}@${pendingDelete.domain}` : ""
        }" wirklich entfernen?`}
        confirmText="Löschen"
      />
    </div>
  );
}

export function EnrolmentWrapper(): JSX.Element {
  const [emailEnrollmentPolicy, setEmailEnrollmentPolicy] = useState<EmailEnrollment | undefined>(
    undefined
  );

  // useEffect(() => {
  //   (async () => {
  //     try {
  //       const res = await idpc.AuthStart({
  //         authCtx: {
  //           authenticationContextClass: [],
  //           maxAgeSeconds: 99999999,
  //         },
  //       });

  //       console.log("Email enrollment policy:", res.authContinue?.emailEnrollment);
  //       setEmailEnrollmentPolicy(res.authContinue?.emailEnrollment);
  //     } catch (err) {
  //       console.log(err);
  //     }
  //   })();
  // }, []);

  // if (!emailEnrollmentPolicy) return <Spinner />;

  // TODO: fix
  useEffect(() => {
    setEmailEnrollmentPolicy({
      $type: "idp.EmailEnrollment",
      required: true,
      domains: [],
      notDomains: [],
    });
  }, []);

  return (
    <>
      <EmailEnroll onDone={() => {}} policy={emailEnrollmentPolicy} />
    </>
  );
}

const ItemContainer = styled.div`
  background: #d6d6d6;
  text-align: left;
  display: flex;
  align-items: center;
  border-radius: 25px;
  padding-left: 20px;
  padding-right: 20px;
  height: 50px;
  margin-bottom: 20px;
`;

const DeleteButton = styled.button`
  border: none;
  cursor: pointer;
  background: none;
`;

const ItemDeleteIcon = styled(Icon)`
  color: #57b846;
  :hover {
    background: ${lighten(0.05, "#d6d6d6")};
  }
`;

const ItemText = styled.div`
  flex-grow: 1;
`;

const StatusText = styled.span`
  font-size: 0.8rem;
  color: #555;
  margin-left: 10px;
`;

export function EmailItem(props: {
  spec: EmailAddress;
  onDelete: () => void;
  onVerify: () => void;
  canDelete: boolean;
}): JSX.Element {
  const isVerified = props.spec.trust >= Trust.VERIFIED;

  return (
    <ItemContainer>
      <Icon path={mdiEmail} size={1} style={{ marginRight: "10px" }} />
      <ItemText>
        {props.spec.localPart}@{props.spec.domain}
        {isVerified ? (
          <StatusText>
            <Icon path={mdiShieldCheck} size={0.7} color="green" /> Verified
          </StatusText>
        ) : (
          <StatusText>(Unverified)</StatusText>
        )}
      </ItemText>
      {/* {!isVerified && (
        <div style={{ marginRight: "10px" }}>
          <Button onClick={props.onVerify}>Verify</Button>
        </div>
      )} */}
      <DeleteButton onClick={props.onDelete} disabled={!props.canDelete || props.spec.external}>
        <ItemDeleteIcon
          path={mdiDeleteOutline}
          size={1}
          color={props.canDelete ? "#57b846" : "#ccc"}
        />
      </DeleteButton>
    </ItemContainer>
  );
}
